import type { UseQueryResult } from "@tanstack/react-query";
import isFunction from "lodash/isFunction";
import React, { FC, ReactNode, useContext, useMemo } from "react";

import { IClassroomItem, IClassroomList, useClassroomListQuery } from "@sol/sdk";
import { OrderingDirection } from "@sol/sdk/utils/api";

export type ContextType = Omit<UseQueryResult<IClassroomList, any>, "data" | "error"> & {
    classrooms?: IClassroomItem[];
};

const Context = React.createContext<ContextType | undefined>(undefined);

const { Provider } = Context;

export type ProviderRenderProp = (ctx: ContextType) => ReactNode;

export const ClassroomsProvider: FC<{
    disabled: boolean;
    children: ReactNode | ProviderRenderProp;
    queryKey?: string;
}> = ({ disabled, children, queryKey }) => {
    const { data, error, ...result } = useClassroomListQuery(
        {
            filters: { restricted: true },
            order: { by: "startDate", direction: OrderingDirection.DESC },
        },
        { enabled: !disabled, suspense: true, queryKey: [queryKey], useErrorBoundary: true },
    );

    const context = useMemo<ContextType>(() => {
        const classrooms = data?.["hydra:member"];

        return {
            classrooms,
            ...result,
        };
    }, [data, result]);

    return <Provider value={context}>{children && isFunction(children) ? children(context) : children}</Provider>;
};

export const useAuthenticatedUserClassrooms = () => {
    const context = useContext(Context);

    if (!context) {
        throw new Error("ClassroomsProvider context is missing");
    }

    return context;
};

export default Context;
